import type { CategoryCode, ContractStatus, Partner, PartnerStatus } from '@/types';

type SupplierSeed = Omit<Partner, 'contractFile' | 'contractStart' | 'contractEnd' | 'noDiscountOnSpecial' | 'minResaleRule' | 'rating' | 'origin' | 'markup'> &
  Partial<Pick<Partner, 'contractFile' | 'contractStart' | 'contractEnd' | 'noDiscountOnSpecial' | 'minResaleRule' | 'rating' | 'origin' | 'markup'>>;

const seeds: SupplierSeed[] = [
  {
    id: 's1',
    name: 'MOON PLUS FURNITURE MANUFACTURING LLC',
    shortName: 'MOON PLUS',
    contact: 'Rashid Al Marzooqi',
    role: 'Sales Director',
    phone: '+971 4 338 7712',
    email: 'sales@moonplus-furniture.ae',
    skus: 119,
    units: 897,
    discount: 18,
    lastUpdate: '2026-10-21',
    staleDays: 2,
    status: 'Active',
    warehouse: 'Warehouse 12, Al Quoz Industrial 3',
    city: 'Dubai, UAE',
    contractStatus: 'Signed',
    paymentTerms: 'Net 30 from delivery',
    delivery: 'Delivery',
    reservationPeriod: 'Stock held 48 hours after PO confirmation',
    notes: 'Biggest supplier by spend. Strongest on workstations, storage and executive desks. Ships own transport within Dubai next day.',
    categories: ['workstations', 'desks', 'execDesks', 'storage', 'meetingTables', 'reception', 'partitions', 'components', 'coffeeTables', 'pedestals', 'services'],
    spend2026: 71080,
    totalSpend: 446261.5,
    sharePct: 26.6,
    portalEmail: 'portal@moonplus-furniture.ae',
    portalUsers: 3,
    lastLogin: '2026-10-22 16:41',
    incoterm: 'DDP Dubai',
    leadTime: '3–5 working days',
    minOrder: 1500,
    minResaleRule: 'Not below 1.18 × partner price',
    noDiscountOnSpecial: true,
    rating: 4.8,
  },
  {
    id: 's2',
    name: 'BARAKAT ALSAHRA TECHNICAL SERVICES LLC.',
    shortName: 'BARAKAT',
    contact: 'Imran Sheikh',
    role: 'Key Accounts Manager',
    phone: '+971 6 534 9920',
    email: 'imran@barakatalsahra.ae',
    skus: 130,
    units: 636,
    discount: 16,
    lastUpdate: '2026-10-20',
    staleDays: 3,
    status: 'Active',
    warehouse: 'Industrial Area 12, Warehouse 44',
    city: 'Sharjah, UAE',
    contractStatus: 'Signed',
    paymentTerms: 'Net 45 from delivery',
    delivery: 'Pickup',
    reservationPeriod: 'Stock held 72 hours after PO confirmation',
    notes: 'Second largest catalogue. Table frames and desk components are the strongest line. Pickup only — book transport through the Sharjah run.',
    categories: ['desks', 'workstations', 'storage', 'components', 'pedestals', 'meetingTables', 'execDesks', 'reception', 'partitions', 'services', 'coffeeTables'],
    spend2026: 176846.29,
    totalSpend: 351851.29,
    sharePct: 21,
    portalEmail: 'portal@barakatalsahra.ae',
    portalUsers: 2,
    lastLogin: '2026-10-21 09:08',
    incoterm: 'EXW Sharjah',
    leadTime: '5–7 working days',
    minOrder: 2500,
    minResaleRule: 'Not below 1.16 × partner price',
    noDiscountOnSpecial: true,
    rating: 4.6,
  },
  {
    id: 's3',
    name: 'Ottimo Office Furtniture Factory L.L.C',
    shortName: 'Ottimo',
    contact: 'Marcello Bianchi',
    role: 'Export Sales',
    phone: '+971 6 743 1180',
    email: 'marcello@ottimo-office.ae',
    skus: 13,
    units: 191,
    discount: 22,
    lastUpdate: '2026-10-22',
    staleDays: 1,
    status: 'Active',
    warehouse: 'Plot 18, Ajman Industrial Area 1',
    city: 'Ajman, UAE',
    contractStatus: 'Signed',
    paymentTerms: '50% advance, 50% on delivery',
    delivery: 'Delivery',
    reservationPeriod: 'Stock held 24 hours after PO confirmation',
    notes: 'Best discount in the network but a narrow catalogue: boardroom tables and a small seating range. Factory lead times are reliable.',
    categories: ['meetingTables', 'workstations', 'chairs', 'execDesks'],
    spend2026: 52380,
    totalSpend: 98236,
    sharePct: 5.9,
    portalEmail: 'partners@ottimo-office.ae',
    portalUsers: 1,
    lastLogin: '2026-10-20 11:22',
    incoterm: 'DAP UAE',
    leadTime: '10–14 working days',
    minOrder: 4000,
    minResaleRule: 'Not below 1.22 × partner price',
    noDiscountOnSpecial: false,
    rating: 4.7,
  },
  {
    id: 's4',
    name: 'AL SEHAM FURNITURE L.L.C',
    shortName: 'AL SEHAM',
    contact: 'Yousef Al Seham',
    role: 'Owner',
    phone: '+971 4 267 4410',
    email: 'yousef@alseham-furniture.ae',
    skus: 59,
    units: 242,
    discount: 15,
    lastUpdate: '2026-10-19',
    staleDays: 4,
    status: 'Active',
    warehouse: 'Shop 7, Naif Road, Deira',
    city: 'Dubai, UAE',
    contractStatus: 'Signed',
    paymentTerms: 'Net 15 from delivery',
    delivery: 'Delivery',
    reservationPeriod: 'Stock held 48 hours after PO confirmation',
    notes: 'Broad but shallow stock. Useful for filling small specification gaps quickly. Training chairs are the standout line.',
    categories: ['desks', 'chairs', 'meetingTables', 'storage', 'pedestals', 'workstations', 'execDesks', 'components', 'reception', 'softSeating', 'coffeeTables', 'services'],
    spend2026: 66075,
    totalSpend: 85095,
    sharePct: 5.1,
    portalEmail: 'orders@alseham-furniture.ae',
    portalUsers: 2,
    lastLogin: '2026-10-19 14:02',
    incoterm: 'DDP Dubai',
    leadTime: '4–6 working days',
    minOrder: 1000,
    minResaleRule: 'Not below 1.15 × partner price',
    noDiscountOnSpecial: true,
    rating: 4.2,
  },
  {
    id: 's5',
    name: 'OFFICE TOUCH FURNITURE INDUSTRY LLC.',
    shortName: 'OFFICE TOUCH',
    contact: 'Sameer Khan',
    role: 'Sales Manager',
    phone: '+971 6 552 9087',
    email: 'sameer@officetouch.ae',
    skus: 50,
    units: 96,
    discount: 14,
    lastUpdate: '2026-10-09',
    staleDays: 14,
    status: 'Paused',
    warehouse: 'Warehouse 9, Sharjah Industrial Area 15',
    city: 'Sharjah, UAE',
    contractStatus: 'Expired',
    paymentTerms: 'Net 30 from delivery',
    delivery: 'Pickup',
    reservationPeriod: 'No reservation without deposit',
    notes: 'Contract expired in August. Price list 14 days old — cannot publish until both are resolved. Coffee table stands and labour services are useful.',
    categories: ['workstations', 'desks', 'chairs', 'meetingTables', 'storage', 'execDesks', 'pedestals', 'reception', 'coffeeTables', 'partitions', 'services'],
    spend2026: 76145.95,
    totalSpend: 83800.95,
    sharePct: 5,
    portalEmail: 'accounts@officetouch.ae',
    portalUsers: 0,
    lastLogin: '2026-09-28 10:15',
    incoterm: 'EXW Sharjah',
    leadTime: '6–8 working days',
    minOrder: 2000,
    minResaleRule: 'Not below 1.14 × partner price',
    noDiscountOnSpecial: false,
    rating: 3.9,
  },
  {
    id: 's6',
    name: 'Aoke Space Trading L.L.C',
    shortName: 'Aoke Space',
    contact: 'Li Wei',
    role: 'Export Manager',
    phone: '+971 4 220 8834',
    email: 'liwei@aokespace.ae',
    skus: 5,
    units: 65,
    discount: 20,
    lastUpdate: '2026-10-18',
    staleDays: 5,
    status: 'Active',
    warehouse: 'Unit 6, Dubai Investment Park 1',
    city: 'Dubai, UAE',
    contractStatus: 'Signed',
    paymentTerms: 'Net 30 from delivery',
    delivery: 'Delivery',
    reservationPeriod: 'Stock held 24 hours after PO confirmation',
    notes: 'Only five articles, but they cover a large share of workstation spend. Table bases and cantilever meeting chairs are exclusive to them.',
    categories: ['workstations', 'components'],
    spend2026: 57225,
    totalSpend: 57225,
    sharePct: 3.4,
    portalEmail: 'partner@aokespace.ae',
    portalUsers: 1,
    lastLogin: '2026-10-18 08:47',
    incoterm: 'CIF Jebel Ali',
    leadTime: '7–10 working days',
    minOrder: 3000,
    minResaleRule: 'Not below 1.2 × partner price',
    noDiscountOnSpecial: true,
    rating: 4.5,
  },
  {
    id: 's7',
    name: 'Salem and Abid General Trading co llc',
    shortName: 'Salem & Abid',
    contact: 'Abdul Salem',
    role: 'Partner Manager',
    phone: '+971 6 533 2277',
    email: 'abdul@salemandabid.ae',
    skus: 26,
    units: 95,
    discount: 17,
    lastUpdate: '2026-10-16',
    staleDays: 7,
    status: 'Active',
    warehouse: 'Warehouse 3, Sharjah Industrial Area 4',
    city: 'Sharjah, UAE',
    contractStatus: 'Signed',
    paymentTerms: 'Net 30 from delivery',
    delivery: 'Pickup',
    reservationPeriod: 'Stock held 36 hours after PO confirmation',
    notes: 'Long-standing relationship. Good on storage and partitions, slower on seating. Prices refresh roughly every three weeks.',
    categories: ['desks', 'storage', 'workstations', 'partitions', 'pedestals', 'chairs', 'reception', 'coffeeTables', 'components'],
    spend2026: 35824,
    totalSpend: 46559,
    sharePct: 2.8,
    portalEmail: 'info@salemandabid.ae',
    portalUsers: 1,
    lastLogin: '2026-10-16 13:30',
    incoterm: 'EXW Sharjah',
    leadTime: '5–7 working days',
    minOrder: 1200,
    minResaleRule: 'Not below 1.17 × partner price',
    noDiscountOnSpecial: true,
    rating: 4.3,
  },
  {
    id: 's8',
    name: 'FUTURE WORKPLACE OFFICE FURNITURE TRADING CO LLC',
    shortName: 'Future Workplace',
    contact: 'Hassan Al Ali',
    role: 'Commercial Manager',
    phone: '+971 4 386 5540',
    email: 'hassan@futureworkplace.ae',
    skus: 22,
    units: 57,
    discount: 19,
    lastUpdate: '2026-10-22',
    staleDays: 1,
    status: 'Active',
    warehouse: 'Warehouse 21, Dubai Investment Park 2',
    city: 'Dubai, UAE',
    contractStatus: 'Signed',
    paymentTerms: 'Net 45 from delivery',
    delivery: 'Delivery',
    reservationPeriod: 'Stock held 72 hours after PO confirmation',
    notes: 'Main soft seating source. Executive PU chairs and reception sofas are consistently in stock. Contract-grade fabrics available.',
    categories: ['softSeating', 'chairs', 'services'],
    spend2026: 42220,
    totalSpend: 42220,
    sharePct: 2.5,
    portalEmail: 'partners@futureworkplace.ae',
    portalUsers: 2,
    lastLogin: '2026-10-22 17:05',
    incoterm: 'DDP Dubai',
    leadTime: '4–6 working days',
    minOrder: 2000,
    minResaleRule: 'Not below 1.19 × partner price',
    noDiscountOnSpecial: false,
    rating: 4.4,
  },
  {
    id: 's9',
    name: 'SKY OFFICE FURNITURE MANUFACTURING LLC',
    shortName: 'SKY Office',
    contact: 'Mohammed Sky',
    role: 'Factory Sales',
    phone: '+971 6 745 3321',
    email: 'sales@skyoffice-furniture.ae',
    skus: 22,
    units: 92,
    discount: 13,
    lastUpdate: '2026-10-21',
    staleDays: 2,
    status: 'Active',
    warehouse: 'Plot 26, Ajman Free Zone',
    city: 'Ajman, UAE',
    contractStatus: 'Signed',
    paymentTerms: 'Net 30 from delivery',
    delivery: 'Delivery',
    reservationPeriod: 'Stock held 24 hours after PO confirmation',
    notes: 'Sofas and reception units made locally, so re-upholstery is possible. Lowest discount but short lead times on custom fabric.',
    categories: ['softSeating', 'services', 'chairs', 'reception'],
    spend2026: 23375,
    totalSpend: 40400,
    sharePct: 2.4,
    portalEmail: 'orders@skyoffice-furniture.ae',
    portalUsers: 1,
    lastLogin: '2026-10-21 12:19',
    incoterm: 'DAP UAE',
    leadTime: '12–16 working days',
    minOrder: 3500,
    minResaleRule: 'Not below 1.13 × partner price',
    noDiscountOnSpecial: true,
    rating: 4,
  },
  {
    id: 's10',
    name: 'SOLO CLASS GENERAL TRADING LLC',
    shortName: 'SOLO CLASS',
    contact: 'Elena Petrova',
    role: 'Sales Lead',
    phone: '+971 6 531 7744',
    email: 'elena@soloclass.ae',
    skus: 32,
    units: 145,
    discount: 21,
    lastUpdate: '2026-10-21',
    staleDays: 2,
    status: 'Active',
    warehouse: 'Warehouse 8, Sharjah Industrial Area 6',
    city: 'Sharjah, UAE',
    contractStatus: 'Signed',
    paymentTerms: 'Net 15 from delivery',
    delivery: 'Pickup',
    reservationPeriod: 'Stock held 48 hours after PO confirmation',
    notes: 'Storage and pedestals specialist with the deepest drawer stock. Good discount, pickup only, palletised packing.',
    categories: ['storage', 'pedestals', 'services'],
    spend2026: 21042.7,
    totalSpend: 40372.89,
    sharePct: 2.4,
    portalEmail: 'b2b@soloclass.ae',
    portalUsers: 2,
    lastLogin: '2026-10-21 10:34',
    incoterm: 'EXW Sharjah',
    leadTime: '6–9 working days',
    minOrder: 1800,
    minResaleRule: 'Not below 1.21 × partner price',
    noDiscountOnSpecial: true,
    rating: 4.5,
  },
  {
    id: 's11',
    name: 'CATCH WORD FURNITURE FZCO',
    shortName: 'Catch Word',
    contact: 'Daniel Osei',
    role: 'Account Manager',
    phone: '+971 4 887 1122',
    email: 'daniel@catchword-furniture.ae',
    skus: 16,
    units: 135,
    discount: 12,
    lastUpdate: '2026-10-20',
    staleDays: 3,
    status: 'Negotiating',
    warehouse: 'Unit 14, Dubai Silicon Oasis',
    city: 'Dubai, UAE',
    contractStatus: 'Sent',
    paymentTerms: '50% advance, 50% on delivery',
    delivery: 'Delivery',
    reservationPeriod: 'Stock held 12 hours',
    notes: 'Negotiating discount up to 16%. Contract sent, waiting for signature. Desks and pedestals are competitively priced.',
    categories: ['desks', 'pedestals', 'chairs', 'meetingTables', 'components'],
    spend2026: 34496.35,
    totalSpend: 34496.35,
    sharePct: 2.1,
    portalEmail: 'sales@catchword-furniture.ae',
    portalUsers: 1,
    lastLogin: '2026-10-20 15:48',
    incoterm: 'DDP Dubai',
    leadTime: '5–8 working days',
    minOrder: 1500,
    minResaleRule: 'Not below 1.12 × partner price',
    noDiscountOnSpecial: false,
    rating: 4.1,
  },
  {
    id: 's12',
    name: 'EVEREST FURNITURES TRADING LLC.',
    shortName: 'Everest',
    contact: 'Nabil Haddad',
    role: 'Managing Partner',
    phone: '+971 4 259 6633',
    email: 'nabil@everestfurnitures.ae',
    skus: 21,
    units: 249,
    discount: 10,
    lastUpdate: '2026-10-13',
    staleDays: 10,
    status: 'Active',
    warehouse: 'Warehouse 5, Ras Al Khor Industrial 2',
    city: 'Dubai, UAE',
    contractStatus: 'Signed',
    paymentTerms: 'Net 30 from delivery',
    delivery: 'Delivery',
    reservationPeriod: 'Stock held 24 hours after PO confirmation',
    notes: 'Very deep seating stock — the go-to supplier for large chair quantities. Discount is low but availability is the best in the network.',
    categories: ['chairs', 'desks', 'services'],
    spend2026: 29008.25,
    totalSpend: 29227.3,
    sharePct: 1.7,
    portalEmail: 'sales@everestfurnitures.ae',
    portalUsers: 1,
    lastLogin: '2026-10-13 09:52',
    incoterm: 'DDP Dubai',
    leadTime: '2–4 working days',
    minOrder: 1000,
    minResaleRule: 'Not below 1.1 × partner price',
    noDiscountOnSpecial: true,
    rating: 4.6,
  },
  {
    id: 's13',
    name: 'Al Kanz Office Furniture',
    shortName: 'Al Kanz',
    contact: 'Ahmed Al Kanz',
    role: 'Sales Manager',
    phone: '+971 50 447 2210',
    email: 'ahmed@alkanz-furniture.ae',
    skus: 12,
    units: 64,
    discount: 18,
    lastUpdate: '2026-10-21',
    staleDays: 2,
    status: 'Active',
    warehouse: 'Warehouse No.6, Industrial Area 18',
    city: 'Sharjah, UAE',
    contractStatus: 'Signed',
    paymentTerms: 'Net 30 from delivery',
    delivery: 'Delivery',
    reservationPeriod: 'Stock held 48 hours after PO confirmation',
    notes: 'Small but reliable: mesh task chairs and table components. Weekly stock refresh on Tuesdays, invoices by WhatsApp.',
    categories: ['chairs', 'components'],
    spend2026: 15796,
    totalSpend: 15796,
    sharePct: 0.9,
    portalEmail: 'ahmed@alkanz-furniture.ae',
    portalUsers: 1,
    lastLogin: '2026-10-21 18:24',
    incoterm: 'DDP UAE',
    leadTime: '3–5 working days',
    minOrder: 800,
    minResaleRule: 'Not below 1.18 × partner price',
    noDiscountOnSpecial: true,
    rating: 4.8,
  },
];

export const partners: Partner[] = seeds.map((seed) => ({
  contractFile: `Treejar-${seed.shortName.replace(/\s+/g, '')}-Agreement-2026.pdf`,
  contractStart: '2026-01-01',
  contractEnd: '2026-12-31',
  noDiscountOnSpecial: true,
  minResaleRule: 'Not below 1.15 × partner price',
  rating: 4.3,
  origin: 'China',
  markup: 40,
  ...seed,
}));

export const contractStatusOf = (status: ContractStatus) => status;
export const partnerStatusOf = (status: PartnerStatus) => status;

export const priceListHistory = [
  { partnerId: 's1', version: 12, date: '2026-10-21', items: 119, changes: 12, uploadedBy: 'Layla Hassan', status: 'Published' },
  { partnerId: 's1', version: 11, date: '2026-10-07', items: 114, changes: 9, uploadedBy: 'Layla Hassan', status: 'Superseded' },
  { partnerId: 's1', version: 10, date: '2026-09-22', items: 110, changes: 4, uploadedBy: 'Layla Hassan', status: 'Superseded' },
  { partnerId: 's2', version: 9, date: '2026-10-20', items: 130, changes: 7, uploadedBy: 'Omar Sultan', status: 'Published' },
  { partnerId: 's2', version: 8, date: '2026-09-29', items: 126, changes: 14, uploadedBy: 'Layla Hassan', status: 'Superseded' },
  { partnerId: 's3', version: 6, date: '2026-10-22', items: 13, changes: 2, uploadedBy: 'Layla Hassan', status: 'Published' },
  { partnerId: 's4', version: 8, date: '2026-10-19', items: 59, changes: 6, uploadedBy: 'Omar Sultan', status: 'Published' },
  { partnerId: 's5', version: 7, date: '2026-10-09', items: 50, changes: 3, uploadedBy: 'Layla Hassan', status: 'Published' },
  { partnerId: 's6', version: 4, date: '2026-10-18', items: 5, changes: 1, uploadedBy: 'Layla Hassan', status: 'Published' },
  { partnerId: 's7', version: 5, date: '2026-10-16', items: 26, changes: 4, uploadedBy: 'Layla Hassan', status: 'Published' },
  { partnerId: 's8', version: 3, date: '2026-10-22', items: 22, changes: 5, uploadedBy: 'Omar Sultan', status: 'Published' },
  { partnerId: 's9', version: 3, date: '2026-10-21', items: 22, changes: 2, uploadedBy: 'Layla Hassan', status: 'Published' },
  { partnerId: 's10', version: 4, date: '2026-10-21', items: 32, changes: 6, uploadedBy: 'Layla Hassan', status: 'Published' },
  { partnerId: 's11', version: 2, date: '2026-10-20', items: 16, changes: 3, uploadedBy: 'Omar Sultan', status: 'Draft' },
  { partnerId: 's12', version: 6, date: '2026-10-13', items: 21, changes: 1, uploadedBy: 'Layla Hassan', status: 'Published' },
  { partnerId: 's13', version: 11, date: '2026-10-21', items: 12, changes: 2, uploadedBy: 'Layla Hassan', status: 'Published' },
];

export const networkTotals = {
  articles: 649,
  units: 4000,
  spend: 1674975.47,
  spend2026: 818546.93,
  suppliers: 56,
};

export const supplierCategories: CategoryCode[] = [
  'workstations',
  'desks',
  'chairs',
  'storage',
  'components',
  'meetingTables',
  'softSeating',
  'pedestals',
  'execDesks',
  'partitions',
  'reception',
  'coffeeTables',
];